<template>
  <div class="pb-page narrow sv">
    <PageHeader title="Settings" subtitle="Your workspace, plan, exports and requests from the public." />

    <!-- Nothing role- or plan-specific until the workspace is known (a direct load would otherwise show an owner a
         teammate's "Leave this workspace" on a Free plan for a moment). -->
    <div v-if="loadFailed" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i>
      <span>{{ $store.getters['praiseboard/loadError'] || 'We couldn\'t load your settings. Check your connection and try again.' }}</span>
      <button type="button" class="pb-button sm outline" @click="refreshWorkspace">Retry</button></div>
    <SkeletonList v-else-if="!ready" :rows="3" label="Loading your settings…" />
    <template v-else>
      <section class="pb-card sv-card" aria-labelledby="sv-ws">
        <h2 id="sv-ws" class="pb-section-title display-font sv-h">Workspace</h2>
        <form v-if="isOwner" @submit.prevent="saveWorkspace">
          <div class="pb-field"><label class="pb-label" for="sv-name">{{ wsForm.kind === 'agency' ? 'Agency name' : 'Name' }}</label><input id="sv-name" v-model="wsForm.name" class="pb-input" maxlength="120" required>
            <span class="pb-help">{{ wsForm.kind === 'agency' ? 'Your clients see it on their approval and report pages.' : 'Shown on your Overview and to teammates you invite.' }}</span></div>
          <div class="pb-field"><span id="sv-kind-l" class="pb-label">This workspace is for</span>
            <div class="pb-segmented sv-kind" role="radiogroup" aria-labelledby="sv-kind-l">
              <button type="button" role="radio" :aria-checked="wsForm.kind === 'business' ? 'true' : 'false'" :aria-pressed="wsForm.kind === 'business' ? 'true' : 'false'" @click="wsForm.kind = 'business'">My own business</button>
              <button type="button" role="radio" :aria-checked="wsForm.kind === 'agency' ? 'true' : 'false'" :aria-pressed="wsForm.kind === 'agency' ? 'true' : 'false'" @click="wsForm.kind = 'agency'">An agency with clients</button>
            </div></div>
          <div class="pb-field"><span class="pb-label">Workspace logo <span class="optional">(for approval and report pages)</span></span>
            <LogoPicker v-model="logo" :current="wsLogo" :status="logoStatus" @input="uploadLogo" /></div>
          <button type="submit" class="pb-button" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </form>
        <div v-else>
          <p class="pb-body"><strong>{{ ws.workspace.name }}</strong> · you're an {{ ws.role === 'admin' ? 'admin' : 'editor' }}.</p>
          <p class="pb-muted">Billing is managed by {{ ws ? ws.owner_email : 'the owner' }}.</p>
          <button type="button" class="pb-button outline sv-leave" @click="leave">Leave this workspace</button>
        </div>
      </section>

      <section id="plan" class="pb-card sv-card" aria-labelledby="sv-plan">
        <div class="pb-card-head"><h2 id="sv-plan" class="pb-section-title display-font">{{ planLabel }} plan</h2>
          <router-link v-if="isOwner" to="/subscription" class="pb-button sm outline">Subscription &amp; billing</router-link></div>
        <div v-if="overLimit" class="pb-alert" role="status"><i class="mdi mdi-alert-outline pb-ico" aria-hidden="true"></i>
          <div><p><strong>{{ overLimit.enforced ? 'Only what fits your plan is public now.' : 'Nothing has changed publicly yet.' }}</strong>
            {{ overLimit.enforced ? 'Since ' : 'From ' }}{{ formatDate(overLimit.effective_on) }}, within the {{ planLabel }} plan's limits:</p>
            <ul class="sv-over"><li v-for="effect in overEffects" :key="effect">{{ effect.charAt(0).toUpperCase() + effect.slice(1) }}</li></ul>
            <p>To choose what stays public, unpublish testimonials, pause widgets or archive brands until you're within the limits{{ isOwner ? ', or upgrade' : '' }}. A downgrade never deletes anything.</p></div></div>
        <p v-if="scopedToBrands" class="pb-muted">In the {{ brands.length === 1 ? 'brand' : brands.length + ' brands' }} you work on.</p>
        <div class="sv-meters">
          <LimitMeter label="Published testimonials" :used="usage.published || 0" :allowed="lim('published')" />
          <LimitMeter v-if="!scopedToBrands" label="Active brands" :used="usage.brands || 0" :allowed="lim('brands')" />
          <LimitMeter label="Collection forms" :used="usage.forms || 0" :allowed="lim('forms')" />
          <LimitMeter label="Widgets" :used="usage.widgets || 0" :allowed="lim('widgets')" />
          <LimitMeter label="Team seats" :used="usage.seats || 1" :allowed="lim('seats')" />
        </div>
        <details class="sv-lower"><summary>What changes on a lower plan</summary>
          <p class="pb-body">Nothing is deleted, and you get 30 days to choose which testimonials, brands and widgets stay public. After that, public pages show the oldest items within the lower plan's limits, other layouts show as a grid and the TrustClap credit returns. Your inbox, private feedback and exports stay fully available.</p></details>
        <p class="pb-muted sv-fair">Fair use on every plan: {{ fair.forms_per_brand }} forms and {{ fair.widgets_per_brand }} widgets per brand, {{ fair.submissions_per_form_per_day }} responses per form per day.</p>
      </section>

      <section class="pb-card sv-card" aria-labelledby="sv-export">
        <h2 id="sv-export" class="pb-section-title display-font sv-h">Export</h2>
        <p class="pb-body">A CSV of every response with its permission record, status, quote and answers. Works on every plan.</p>
        <div v-if="can('export')" class="pb-actions sv-exports">
          <button type="button" class="pb-button outline" :disabled="exporting" @click="exportBrand"><i class="mdi mdi-download pb-ico" aria-hidden="true"></i>{{ brand ? brand.name : 'Brand' }} (CSV)</button>
          <button v-if="multiBrand" type="button" class="pb-button outline" :disabled="exporting" @click="exportAll"><i class="mdi mdi-download pb-ico" aria-hidden="true"></i>All brands (CSV)</button>
        </div>
        <p v-else class="pb-muted">Ask an owner or admin to export.</p>
      </section>

      <section id="removals" class="pb-card sv-card" aria-labelledby="sv-removals">
        <h2 id="sv-removals" class="pb-section-title display-font sv-h">Removal requests</h2>
        <p v-if="!requests.length" class="pb-muted">None open. People can ask for a testimonial to be removed from your hosted wall; requests appear here.</p>
        <div v-for="request in requests" :key="request.id" class="sv-request">
          <p class="sv-req-who"><strong>{{ request.name }}</strong> · <a :href="'mailto:' + request.email">{{ request.email }}</a><span class="pb-muted"> · {{ request.brand_name }} · {{ formatDate(request.created_at) }}</span></p>
          <p v-if="request.message" class="sv-req-msg">"{{ request.message }}"</p>
          <p v-if="request.testimonial" class="pb-muted">About: "{{ request.testimonial.excerpt }}" ({{ request.testimonial.name || 'Anonymous' }})</p>
          <div v-else class="pb-field sv-req-pick"><label class="pb-label" :for="'sv-pick-' + request.id">Which testimonial?</label>
            <select :id="'sv-pick-' + request.id" v-model.number="picks[request.id]" class="pb-select" @focus="loadPublished(request.brand_id)">
              <option :value="undefined">Choose one</option>
              <option v-for="item in published[request.brand_id] || []" :key="item.id" :value="item.id">{{ item.name || 'Anonymous' }}: {{ item.excerpt }}</option></select></div>
          <div class="pb-actions">
            <button type="button" class="pb-button sm" :disabled="!request.testimonial && !picks[request.id]" @click="resolve(request, 'withdraw')">Remove the testimonial</button>
            <button type="button" class="pb-button sm ghost" @click="resolve(request, 'dismiss')">Dismiss</button>
          </div>
        </div>
      </section>

      <section v-if="brand && !brand.privacy_url && can('brands')" class="pb-note sv-privacy"><i class="mdi mdi-shield-account-outline pb-ico" aria-hidden="true"></i>
        <span>Add your privacy policy link so collection pages can point customers to it. <router-link :to="'/brands/' + brand.id">Brand settings</router-link></span></section>
    </template>
  </div>
</template>

<script>
import LimitMeter from '../components/LimitMeter.vue';
import LogoPicker from '../components/LogoPicker.vue';
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import workspace from '../mixins/workspace';
import { download, get, patch, post, remove, message } from '../api';
import { formatDate } from '../lib/format';
import { overLimitEffects } from '../lib/overLimit';
import { uploadImage, waitForImage } from '../lib/images';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';
import { imageUrl } from '../publicUrls';

export default {
  name: 'SettingsView',
  components: { LimitMeter, LogoPicker, PageHeader, SkeletonList },
  mixins: [workspace],
  data: () => ({ wsForm: { name: '', kind: 'business' }, saving: false, logo: null, logoStatus: '', exporting: false, requests: [], picks: {}, published: {},
    initialized: false }),
  computed: {
    // As on the Brands page: the page waits for the workspace, or says it couldn't load it.
    ready() { return this.$store.getters['praiseboard/loaded'] && !!this.ws && !!this.ws.workspace; },
    loadFailed() { return this.$store.getters['praiseboard/failed'] && !this.ws; },
    overLimit() { return this.$store.getters['praiseboard/overLimit']; },
    overEffects() { return overLimitEffects(this.overLimit); },
    wsLogo() { const logo = this.ws && this.ws.workspace && this.ws.workspace.logo; return logo && logo.status === 'complete' ? imageUrl(logo.key) : ''; },
    fair() { return (this.ws && this.ws.fair_use) || {}; }
  },
  // Also after a Retry, once the workspace arrives.
  watch: { ready(value) { if (value) this.init(); } },
  async mounted() {
    const data = await this.requireWorkspace();
    if (!data) return;
    this.init();
    if (this.$route.hash) this.$nextTick(() => { const el = document.querySelector(this.$route.hash); if (el) el.scrollIntoView(); });
  },
  methods: {
    formatDate,
    init() {
      if (this.initialized) return;
      this.initialized = true;
      this.wsForm = { name: this.ws.workspace.name, kind: this.ws.workspace.kind };
      this.loadRequests();
    },
    lim(key) { const value = this.limits[key]; return value === undefined ? null : value; },
    async saveWorkspace() {
      this.saving = true;
      try { await patch('/workspace', { name: this.wsForm.name.trim(), kind: this.wsForm.kind }); await this.refreshWorkspace(); toast('Saved.'); } catch (error) { toast(message(error), 'error'); } finally { this.saving = false; }
    },
    async uploadLogo(picked) {
      if (!picked || !picked.file) return;
      this.logoStatus = 'Uploading…';
      try {
        const image = await uploadImage('/workspace/logo', picked.file);
        this.logoStatus = 'Processing…';
        const done = await waitForImage(image);
        this.logoStatus = done && done.status === 'failed' ? (done.error || 'That image couldn\'t be read.') : '';
        if (!this.logoStatus) this.logo = null;
        await this.refreshWorkspace();
      } catch (error) { this.logoStatus = message(error); }
    },
    async leave() {
      if (!(await ask('Leave ' + this.ws.workspace.name + '? You lose access at once.', { action: 'Leave', tone: 'danger' }))) return;
      try {
        await remove('/members/' + this.ws.member.id);
        this.$store.commit('praiseboard/RESET');
        this.$router.push('/start').catch(() => {});
      } catch (error) { toast(message(error), 'error'); }
    },
    async exportBrand() {
      this.exporting = true;
      try { await download('/brands/' + this.brandId + '/export.csv', 'trustclap-' + this.brand.slug + '.csv'); } catch (error) { toast(message(error), 'error'); } finally { this.exporting = false; }
    },
    async exportAll() {
      this.exporting = true;
      try { await download('/workspace/export.csv', 'trustclap-export.csv'); } catch (error) { toast(message(error), 'error'); } finally { this.exporting = false; }
    },
    async loadRequests() {
      if (!this.can('removals')) return;
      try { this.requests = (await get('/removal-requests', { status: 'open' })).requests || []; } catch (error) { this.requests = []; }
    },
    async loadPublished(brandId) {
      if (this.published[brandId]) return;
      try { this.$set(this.published, brandId, (await get('/brands/' + brandId + '/submissions', { status: 'published' })).items || []); } catch (error) { this.$set(this.published, brandId, []); }
    },
    async resolve(request, action) {
      if (action === 'withdraw' && !(await ask('Remove this testimonial from every widget and the wall? It stays in your inbox as private.', { action: 'Remove it', tone: 'danger' }))) return;
      try {
        await post('/removal-requests/' + request.id + '/resolve', { action, submission_id: request.testimonial ? null : (this.picks[request.id] || null) });
        toast(action === 'withdraw' ? 'Removed. It leaves your widgets and wall within a minute.' : 'Request dismissed.');
        this.loadRequests();
        this.refreshWorkspace();
      } catch (error) { toast(message(error), 'error'); }
    }
  }
};
</script>

<style scoped>
/* Phones: one full-width row per option, so the second never wraps under the first at its own width. */
@media(max-width:480px){.sv-kind{display:grid;grid-template-columns:minmax(0,1fr);width:100%}.sv-kind button{text-align:left;padding-left:16px}}
.sv-card{margin-bottom:18px}
.sv-h{margin-bottom:14px}
.sv-leave{margin-top:12px}
.sv-over{margin:6px 0 8px;padding-left:18px}
.sv-meters{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(240px,100%),1fr));gap:16px;margin-top:6px}
.sv-lower{margin-top:18px}
.sv-lower summary{cursor:pointer;min-height:44px;display:inline-flex;align-items:center;font-weight:600;color:#963A22}
.sv-fair{margin-top:12px}
.sv-exports{margin-top:14px}
.sv-request{padding:14px 0;border-top:1px solid #EFE9E0}
.sv-request:first-of-type{border-top:0}
.sv-req-who{margin:0 0 4px;font-size:14.5px}
.sv-req-msg{margin:6px 0;font:500 15px/1.5 var(--font-display)!important;color:#1F2328}
.sv-req-pick{margin:10px 0}
.sv-request .pb-actions{margin-top:10px}
.sv-privacy{margin-top:0}
</style>
